import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import styled from 'styled-components';
import CS from '@/styles/componentStyled';
import { Facilities } from '@/sdk/data-contracts';
import { dashboardFacilityRecoil } from '@/atom/DashboardAtom';
import { Map, MapMarker } from 'react-kakao-maps-sdk';
import { MapLoadingRecoil } from '@/atom/ModalAtom';
import MapLoading from '@/component/MapLoading';

const TabFacility = () => {
  const dashboardFacilityState = useRecoilValue(dashboardFacilityRecoil);

  const [mapLoadingRecoil, setMapLoadingState] =
    useRecoilState(MapLoadingRecoil);

  const [markers, setMarkers] = useState<{ lat: number; lng: number }[]>([]);
  const [center, setCenter] = useState({ lat: 37.5665, lng: 126.978 });
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number>(0);
  const [selectFacility, setSelectFacility] = useState<Facilities>();

  useEffect(() => {
    if (dashboardFacilityState) {
      setSelectFacility(dashboardFacilityState.facilities[0]);
    }
  }, [dashboardFacilityState]);

  useEffect(() => {
    const fetchCoordinates = async () => {
      const kakao = (window as any).kakao;
      if (!kakao || !kakao.maps) {
        console.error('Kakao Maps API가 로드되지 않았습니다.');
        return;
      }

      const geocoder = new kakao.maps.services.Geocoder();
      const newMarkers: { lat: number; lng: number }[] = [];

      if (dashboardFacilityState?.facilities) {
        setMapLoadingState(true);
        for (let i = 0; i < dashboardFacilityState.facilities.length; i++) {
          const facility = dashboardFacilityState.facilities[i];
          await new Promise<void>((resolve) => {
            geocoder.addressSearch(
              facility.address,
              (result: any, status: any) => {
                if (status === kakao.maps.services.Status.OK) {
                  const newMarker = {
                    lat: parseFloat(result[0].y),
                    lng: parseFloat(result[0].x),
                  };
                  if (i === 0) {
                    setCenter(newMarker);
                  }
                  newMarkers.push(newMarker);
                } else {
                  console.error(`주소 검색 실패: ${facility.address}`);
                }
                resolve();
              },
            );
          });
        }
      }

      setMarkers(newMarkers);
      setMapLoadingState(false);
    };

    fetchCoordinates();
  }, [dashboardFacilityState]);

  return (
    <>
      <TableBox>
        <table>
          <tbody>
            <tr>
              <th>시설명</th>
              <td>{selectFacility?.name}</td>
              <th>디바이스</th>
              <td>{selectFacility?.deviceCount.toLocaleString()}</td>
            </tr>
            <tr>
              <th>시설 번호</th>
              <td>{selectFacility?.number}</td>
              <th>이상 디바이스</th>
              <td>{selectFacility?.errorDeviceCount.toLocaleString()}</td>
            </tr>
            <tr>
              <th>주소</th>
              <td>{selectFacility?.address}</td>
              <th>통신 실패</th>
              <td>
                {selectFacility?.communicationErrorCount.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </TableBox>
      <MapBox>
        <Map
          center={center}
          style={{ width: '100%', height: '100%' }}
          level={3}
          draggable={true}
          zoomable={true}
        >
          {markers.map((position, index) => (
            <MapMarker
              key={index}
              position={position}
              image={{
                src:
                  selectedMarkerIndex === index
                    ? '/img/ico_marker_red.svg'
                    : '/img/ico_marker_blue.svg',
                size: { width: 28, height: 28 },
              }}
              onClick={() => {
                setSelectedMarkerIndex(index);
                setSelectFacility(dashboardFacilityState?.facilities[index]);
              }}
            ></MapMarker>
          ))}
        </Map>
        {mapLoadingRecoil && <MapLoading />}
      </MapBox>
    </>
  );
};

export default TabFacility;

export const TableBox = styled(CS.TableType1_3)`
  width: 100%;
  min-height: 133px;
  max-height: 133px;
  margin-top: 16px;
  margin-bottom: 16px;
`;

export const MapBox = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  #__react-kakao-maps-sdk___Map {
    z-index: 1;
  }
`;
